import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { SITE } from "@/data/site";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    desc: "How Ebikes&Gear collects, uses and protects your personal data.",
    sections: [
      ["Introduction", `Ebikes&Gear ("we", "us") is committed to protecting your privacy. This policy explains how we collect, use and safeguard your personal information when you use our website and services, in accordance with the UK GDPR and Data Protection Act 2018.`],
      ["Information we collect", "We collect information you provide directly — such as your name, email, address, phone number and payment details — as well as data collected automatically, including your IP address, browser type and browsing behaviour via cookies."],
      ["How we use your data", "We use your information to process orders, deliver products, provide customer support, send order updates, and (with your consent) marketing communications. We never sell your personal data to third parties."],
      ["Payment security", "We accept payment by direct bank transfer only. After placing your order you'll receive our bank details by email — use your order number as the payment reference. No card details are ever collected or stored."],
      ["Your rights", "You have the right to access, correct, delete or restrict the processing of your personal data, and to withdraw consent at any time. To exercise these rights, contact us at " + SITE.email + "."],
      ["Cookies", "We use cookies to improve your experience. See our Cookie Policy for details on the cookies we use and how to manage them."],
      ["Contact", `If you have any questions about this policy, email us at ${SITE.email} or write to us at ${SITE.address}.`],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    desc: "The terms governing your use of the Ebikes&Gear website and services.",
    sections: [
      ["Agreement", "By accessing and using this website, you agree to be bound by these Terms & Conditions and our Privacy Policy. If you do not agree, please do not use our website."],
      ["Products & pricing", "We make every effort to display product details and prices accurately. Prices are in GBP and include VAT where applicable. We reserve the right to correct errors and to change prices without prior notice."],
      ["Orders", "Your order constitutes an offer to purchase. A contract is formed only when we dispatch your goods. We reserve the right to refuse or cancel any order."],
      ["Payment", "Payment is required in full at the time of order. We accept major cards and selected finance options via our payment partners."],
      ["Warranty", "Our electric bikes carry a 5-year frame warranty and a 2-year battery and motor warranty, subject to correct use and servicing. Accessories carry 1–3 year warranties."],
      ["Liability", "To the fullest extent permitted by law, our liability is limited to the value of the goods purchased. We are not liable for indirect or consequential losses."],
      ["Governing law", "These terms are governed by the laws of England and Wales and subject to the exclusive jurisdiction of its courts."],
    ],
  },
  shipping: {
    title: "Shipping Policy",
    desc: "Delivery options, timescales and costs for UK and European orders.",
    sections: [
      ["UK delivery", "We offer free, fully-insured delivery on all bikes across mainland UK, typically within 3–5 working days. Accessories are dispatched within 1–2 working days."],
      ["European delivery", "We ship to over 12 European countries. EU delivery typically takes 5–10 working days. Shipping costs are calculated at checkout based on destination."],
      ["Tracking", "You'll receive a tracking link by email as soon as your order is dispatched, so you can follow your delivery every step of the way."],
      ["Assembly", "Bikes arrive 90% assembled. Final assembly (handlebars, pedals, front wheel) takes around 15 minutes with the included tools and instructions."],
      ["Delivery issues", "If your order arrives damaged or is delayed, contact us immediately at " + SITE.email + " and we'll make it right."],
    ],
  },
  returns: {
    title: "Returns & Refunds",
    desc: "Our hassle-free returns policy for total peace of mind.",
    sections: [
      ["14-day returns", "We offer a 14-day no-quibble return on unused bikes in their original condition and packaging. Simply contact us to arrange a return."],
      ["How to return", "Email " + SITE.email + " with your order number to start a return. We'll provide instructions and, where applicable, arrange collection."],
      ["Refunds", "Once we receive and inspect your return, we'll process your refund to the original payment method within 5–10 working days."],
      ["Exclusions", "For hygiene and safety reasons, certain items (e.g. used helmets) cannot be returned unless faulty. Custom-built bikes are non-returnable unless faulty."],
      ["Faulty goods", "If an item is faulty, you're entitled to a repair, replacement or refund in line with your statutory rights. Our warranty covers manufacturing defects."],
    ],
  },
  cookies: {
    title: "Cookie Policy",
    desc: "How and why Ebikes&Gear uses cookies on this website.",
    sections: [
      ["What are cookies", "Cookies are small text files stored on your device that help websites function and remember your preferences."],
      ["Cookies we use", "We use essential cookies (for cart and checkout), performance cookies (to understand how the site is used), and marketing cookies (to personalise offers, with your consent)."],
      ["Managing cookies", "You can control and delete cookies through your browser settings. Disabling essential cookies may affect site functionality such as your cart and checkout."],
      ["Third-party cookies", "Some cookies are set by third-party services we use, such as payment processing and analytics. These providers have their own privacy policies."],
      ["Updates", "We may update this policy from time to time. Please check back periodically for changes."],
    ],
  },
};

export default function Legal({ type }) {
  const c = CONTENT[type];
  if (!c) return null;
  return (
    <>
      <SEO title={c.title} description={c.desc} path={`/${type}`} />
      <PageHero title={c.title} subtitle={c.desc} crumbs={[{ label: c.title }]} />
      <section className="container-px py-16">
        <div className="mx-auto max-w-3xl space-y-10">
          {c.sections.map(([h, body], i) => (
            <div key={i}>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight">{h}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
          <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</p>
        </div>
      </section>
    </>
  );
}
